<template>
  <div
    v-for="card in cards"
    :key="card.rule.id"
    class="tc-onb-rule-card"
    :class="{ 'tc-onb-rule-card--enabled': card.enabled }"
    :data-testid="`onb-card-${card.rule.id}`"
  >
    <div class="tc-onb-rule-head">
      <span class="tc-onb-rule-title">{{ card.rule.name }}</span>
      <span
        class="tc-onb-chip"
        :class="chipWarns(card) ? 'tc-onb-chip--warn' : 'tc-onb-chip--success'"
      >{{ chipLabel(card) }}</span>
    </div>

    <p v-if="card.description" class="tc-onb-rule-desc">{{ card.description }}</p>

    <template v-if="editing === card">
      <div class="tc-onb-rule-edit">
        <label class="form-label">Name
          <input v-model="card.rule.name" class="form-control form-control-sm" />
        </label>
        <label v-if="card.rule.kind === 'value_to_target'" class="form-label">Terms (comma-separated)
          <input v-model="termsText" class="form-control form-control-sm" />
        </label>
        <label v-if="card.rule.kind === 'value_to_target'" class="form-label">Project code
          <input v-model="card.rule.wbs1" class="form-control form-control-sm" />
        </label>
        <label v-if="card.rule.kind === 'identifier_in_field'" class="form-label">Pattern (optional)
          <input v-model="card.rule.pattern" class="form-control form-control-sm" />
        </label>
      </div>
      <div class="tc-onb-rule-actions">
        <button type="button" class="btn btn-primary btn-sm" @click="applyEdit(card)">Done</button>
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="editing = null">Cancel</button>
      </div>
    </template>

    <div v-else class="tc-onb-rule-actions">
      <button
        type="button"
        class="btn btn-sm"
        :class="card.enabled ? 'btn-outline-secondary' : 'btn-primary'"
        :data-testid="`onb-enable-${card.rule.id}`"
        @click="toggle(card)"
      >{{ card.enabled ? 'Remove' : 'Enable' }}</button>
      <button
        v-if="card.rule.kind !== 'attribute_join'"
        type="button"
        class="btn btn-outline-secondary btn-sm"
        @click="startEdit(card)"
      >Edit</button>
      <button
        v-if="!card.enabled"
        type="button"
        class="btn btn-sm tc-onb-btn-ghost"
        :data-testid="`onb-skip-${card.rule.id}`"
        @click="$emit('skip', card)"
      >Skip</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { MappingRule } from '../api/onboarding';
import { zeroHitReason } from './onboardingRuleLabel';

export interface RuleCard {
  rule: MappingRule;
  hits: number | null;
  sampleSize: number | null;
  fieldPresent: number | null;
  unresolvedCodes: string[];
  enabled: boolean;
  origin: string;
  description?: string;
}

defineProps<{ cards: RuleCard[] }>();
const emit = defineEmits<{
  enable: [card: RuleCard];
  skip: [card: RuleCard];
  edited: [card: RuleCard];
}>();

const editing = ref<RuleCard | null>(null);
const termsText = ref('');

function toggle(card: RuleCard): void {
  if (card.enabled) emit('skip', card);
  else emit('enable', card);
}

function startEdit(card: RuleCard): void {
  editing.value = card;
  termsText.value = card.rule.terms?.join(', ') ?? '';
}

function applyEdit(card: RuleCard): void {
  if (card.rule.kind === 'value_to_target') {
    card.rule.terms = termsText.value.split(',').map(t => t.trim()).filter(Boolean);
  }
  editing.value = null;
  emit('edited', card);
}

// A rule that named a missing code, or ran and matched nothing, must not read
// as a pass.
function chipWarns(card: RuleCard): boolean {
  if (card.unresolvedCodes.length > 0) return true;
  return card.hits === 0 && (card.sampleSize ?? 0) > 0;
}

function chipLabel(card: RuleCard): string {
  if (card.unresolvedCodes.length > 0) return `Code not found: ${card.unresolvedCodes.join(', ')}`;
  if (card.rule.kind === 'attribute_join') return 'Live against Vantagepoint';
  if (card.enabled) return 'Enabled';
  if (card.sampleSize === null) return 'Not yet tested';
  if (card.sampleSize === 0) return 'No recent events to test against';
  // Only explain a zero this run actually measured — see hitsLabel in Onboarding.vue.
  if (card.hits === 0 && card.fieldPresent !== null) {
    return zeroHitReason(card.rule.field, card.sampleSize, card.fieldPresent);
  }
  return `Worked on ${card.hits} of ${card.sampleSize}`;
}
</script>
